import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wifi, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, API_FOR_IMG } from "../App";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cms, setCms] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/cms`)
      .then((res) => setCms(res.data))
      .catch((err) => console.error(err));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });
      const { token, user } = response.data;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      toast.success("Login successful!");

      // Role-based redirect
      if (
        user.role === "admin" ||
        user.role === "super_admin" ||
        user.role === "staff"
      ) {
        navigate("/admin/dashboard");
      } else if (user.role === "client") {
        navigate("/client/dashboard");
      } else if (user.role === "tech") {
        navigate("/tech/tickets");
      } else if (user.role === "agent") {
        navigate("/agent/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const companyName = cms?.company_name || "";
  const companyTagline = cms?.company_tagline || "";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Image (Hidden on mobile, shown on lg+) */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        {cms?.login_bg_image_url ? (
          <img
            src={
              cms.login_bg_image_url.startsWith("http")
                ? cms.login_bg_image_url
                : `${API_FOR_IMG}${cms.login_bg_image_url}`
            }
            alt="Login Background"
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src="https://images.unsplash.com/photo-1710993244013-36a43c328cfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxmaWJlciUyMG9wdGljJTIwaW50ZXJuZXQlMjBhYnN0cmFjdCUyMHNwZWVkfGVufDB8fHx8MTc3MDkyODczNXww&ixlib=rb-4.1.0&q=85"
            alt="Login Background"
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 to-slate-900/90 flex items-center justify-center">
          <div className="text-center text-white px-8 lg:px-12">
            {cms?.logo_url ? (
              <img
                src={
                  cms.logo_url.startsWith("http")
                    ? cms.logo_url
                    : `${API_FOR_IMG}${cms.logo_url}`
                }
                alt={companyName}
                className="h-16 lg:h-20 mx-auto mb-4 lg:mb-6 object-contain"
              />
            ) : (
              <Wifi className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-4 lg:mb-6" />
            )}
            <h1 className="text-3xl lg:text-5xl font-black mb-3 lg:mb-4 tracking-tight">
              {companyName}
            </h1>
            <p className="text-base lg:text-xl text-slate-300">
              {companyTagline}
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Header (Visible only on mobile) */}
      <div className="lg:hidden bg-gradient-to-br from-indigo-900 to-slate-900 px-6 py-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          {cms?.logo_url ? (
            <img
              src={
                cms.logo_url.startsWith("http")
                  ? cms.logo_url
                  : `${API_FOR_IMG}${cms.logo_url}`
              }
              alt={companyName}
              className="h-8 object-contain"
            />
          ) : (
            <Wifi className="w-8 h-8 text-white" />
          )}
          <span className="text-xl font-bold text-white">{companyName}</span>
        </div>
        <p className="text-sm text-slate-300">{companyTagline}</p>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center bg-slate-50 px-4 sm:px-6 py-8 lg:py-12">
        <div className="w-full max-w-md">
          <div className="mb-6 lg:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-sm sm:text-base text-slate-600">
              Sign in to access your account
            </p>
          </div>

          <form
            onSubmit={handleLogin}
            className="space-y-4 sm:space-y-6"
            data-testid="login-form"
          >
            <div>
              <Label
                htmlFor="email"
                className="text-sm font-semibold text-slate-700"
              >
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="mt-1.5 sm:mt-2 h-11 sm:h-12"
              />
            </div>

            <div className="relative">
              <Label
                htmlFor="password"
                className="text-sm font-semibold text-slate-700"
              >
                Password
              </Label>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="mt-1.5 sm:mt-2 h-11 sm:h-12"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 mt-[15px] cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="w-8 h-8" />
                ) : (
                  <Eye className="w-8 h-8" />
                )}
              </button>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 sm:h-12 bg-indigo-700 hover:bg-indigo-800 text-white font-semibold"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          {/* <div className="mt-6 lg:mt-8 p-3 sm:p-4 bg-white border border-slate-200 rounded-lg">
            <p className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">Demo Accounts:</p>
            <p className="text-xs text-slate-600">Admin: admin@isp.com / admin123</p>
            <p className="text-xs text-slate-600">Client: (create via admin panel)</p>
          </div> */}

          <div className="mt-4 lg:mt-6 text-center">
            <Button
              variant="link"
              onClick={() => navigate("/")}
              className="text-indigo-700 text-sm sm:text-base"
              data-testid="back-to-home-btn"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
